/**
 * 1:1 port of new-ui `Header.tsx` (no SportsTicker — that lives in Navebar above).
 * Routes and copy match the source file; icons use `react-icons` instead of lucide.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { LogoutUserFunc } from "../../../redux/actions/User";
import {
  FiBell,
  FiChevronDown,
  FiCompass,
  FiCreditCard,
  FiGift,
  FiHeart,
  FiHelpCircle,
  FiLogOut,
  FiMenu,
  FiSearch,
  FiSettings,
  FiShoppingBag,
  FiStar,
  FiUser,
  FiX,
  FiZap,
} from "react-icons/fi";
import { IoTicketOutline } from "react-icons/io5";
import {
  TbCalendarPlus,
  TbLayoutDashboard,
  TbLayoutGrid,
  TbMask,
  TbMusic,
  TbQrcode,
  TbSparkles,
  TbTrophy,
} from "react-icons/tb";
import { InstaPassLogo } from "./InstaPassLogo";
import { useInstaPassAIChat } from "../../Home/InstaPassAIChatContext";
import { useProfilePage } from "../../UserProfile/ProfileSidebarContext";
import ProfileMobileNavDropdown from "../../UserProfile/ProfileMobileNavDropdown";

const fontStack = { fontFamily: "'Outfit', 'Inter', sans-serif" };

// One account menu: shows what this person uses, and offers the rest.
// `capabilities` comes from the server on login / verify.
const buildProfileMenu = (user) => {
  const caps = user?.capabilities || {};
  const creator = caps.creator; // null | PENDING | APPROVED | ACTIVE | SUSPENDED | REJECTED
  return [
    { label: "My Tickets", icon: IoTicketOutline, href: "/my-tickets" },
    caps.host || user?.userType === "organizer"
      ? { label: "My Events", icon: TbLayoutDashboard, href: "/user/dashboard" }
      : { label: "Host an event", icon: TbCalendarPlus, href: "/create-event/form" },
    creator && creator !== "REJECTED"
      ? { label: creator === "PENDING" ? "Creator (in review)" : "Creator dashboard", icon: TbSparkles, href: "/creators/dashboard" }
      : { label: "Promote & earn", icon: TbSparkles, href: "/creators", accent: true },
    { label: "InstaPoints", icon: FiZap, href: "/instapoints" },
    { label: "My Profile", icon: FiUser, href: "/user/profile" },
    { label: "Help & Support", icon: FiHelpCircle, action: "open-ai-chat" },
  ];
};

const categories = [
  {
    title: "Music",
    icon: TbMusic,
    color: "#E52324",
    subcategories: [
      "Concerts",
      "Rock and Pop",
      "Rap and Hiphop",
      "R&B/Urban Soul",
      "Country/Folk",
      "Jazz/Blues",
      "Alternatives & Indie",
      "Latin",
      "World Music",
      "Festivals",
      "Cabaret",
      "New Age/Spiritual",
    ],
  },
  {
    title: "Arts & Theater",
    icon: TbMask,
    color: "#A855F7",
    subcategories: [
      "Theatres",
      "Musicals",
      "Plays",
      "Opera",
      "Ballet and Dance",
      "Comedy",
      "Award Shows",
      "TV Shows",
      "Special Events",
    ],
  },
  {
    title: "Sports",
    icon: TbTrophy,
    color: "#3B82F6",
    subcategories: [
      "Baseball",
      "MLB",
      "Basketball",
      "NBA",
      "WNBA",
      "Hockey",
      "Football",
      "Soccer",
      "World Cup",
      "Boxing",
      "Wrestling",
      "MMA",
      "NASCAR",
      "Formula 1",
      "Auto Racing",
      "IndyCar Series",
      "Cycling",
      "Extreme Sports",
      "Golf",
      "Tennis",
    ],
  },
  {
    title: "College & League",
    icon: TbSparkles,
    color: "#F59E0B",
    subcategories: [
      "NCAA",
      "NCAA Men",
      "NCAA Women",
      "Minor Leagues",
      "AFL",
      "ECHL",
      "NBA D-League",
      "Mexican League",
      "NLL",
      "Euroleague Basketball",
      "FIBA World Championship",
      "World Basketball Classic",
      "International Friendlies",
      "Highschool",
      "Summer Games",
      "Winter Games",
    ],
  },
  {
    title: "Family & More",
    icon: FiHeart,
    color: "#06B6D4",
    subcategories: [
      "Family",
      "Fundraisers",
      "Supercross",
      "Monster Trucks",
      "Volleyball",
      "Lacrosse",
      "Fighting",
      "Miscellaneous",
    ],
  },
];

const liveCategoryStyleFallback = [
  { icon: TbMusic, color: "#E52324" },
  { icon: TbTrophy, color: "#3B82F6" },
  { icon: TbMask, color: "#A855F7" },
  { icon: TbSparkles, color: "#F59E0B" },
  { icon: FiHeart, color: "#06B6D4" },
];

function resolveLiveCategoryStyle(name, index) {
  const n = (name || "").toLowerCase();
  if (n.includes("sport")) return { icon: TbTrophy, color: "#3B82F6" };
  if (n.includes("concert")) return { icon: TbMusic, color: "#E52324" };
  if (n.includes("theater") || n.includes("theatre")) return { icon: TbMask, color: "#A855F7" };
  if (n.includes("festiv")) return { icon: TbSparkles, color: "#F59E0B" };
  if (n.includes("theme") || n.includes("park")) return { icon: FiHeart, color: "#06B6D4" };
  if (n.includes("comed")) return { icon: TbMask, color: "#F472B6" };
  if (n.includes("movie")) return { icon: TbMask, color: "#EC4899" };
  return liveCategoryStyleFallback[index % liveCategoryStyleFallback.length];
}

function buildDisplayUser(reduxUser, UserAvatar) {
  if (!reduxUser?.firstName) return null;
  const name =
    [reduxUser.firstName, reduxUser.lastName].filter(Boolean).join(" ").trim() ||
    reduxUser.email ||
    "Account";
  const initials =
    `${reduxUser.firstName?.[0] || ""}${reduxUser.lastName?.[0] || ""}`.toUpperCase() ||
    (reduxUser.email?.[0] || "?").toUpperCase();
  return {
    name,
    email: reduxUser.email,
    avatarUrl: reduxUser.Avatar ? UserAvatar : null,
    initials,
    memberSince: "Member",
    loyaltyPoints: 0,
  };
}

const NewUIMarketplaceHeader = ({
  searchFunction,
  user: reduxUser,
  data: categoryData,
  search,
  setSearch,
  UserAvatar,
  setshowmenu,
} = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const aiChat = useInstaPassAIChat();
  const profilePage = useProfilePage();
  const isOrganizerPortal = location.pathname.startsWith("/organizer");
  const isAdminPortal = location.pathname.startsWith("/admin");
  const isQRStudio = location.pathname.startsWith("/qr-studio");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [categoryDropdownQuery, setCategoryDropdownQuery] = useState("");

  const profileRef = useRef(null);
  const categoriesRef = useRef(null);

  const useSideNavMobile = typeof setshowmenu === "function";
  const displayUser = buildDisplayUser(reduxUser, UserAvatar);
  const filteredProfileMenuItems = buildProfileMenu(reduxUser);
  const searchValue = search ?? "";
  const useLiveCategories = Array.isArray(categoryData) && categoryData.length > 0;

  const categoryDropdownFilter = categoryDropdownQuery.trim().toLowerCase();

  const filteredStaticCategories = useMemo(() => {
    if (!categoryDropdownFilter) return categories;
    return categories
      .map((cat) => ({
        ...cat,
        subcategories: cat.subcategories.filter((sub) =>
          sub.toLowerCase().includes(categoryDropdownFilter)
        ),
      }))
      .filter(
        (cat) =>
          cat.title.toLowerCase().includes(categoryDropdownFilter) ||
          cat.subcategories.length > 0
      );
  }, [categoryDropdownFilter]);

  const filteredLiveCategories = useMemo(() => {
    if (!useLiveCategories) return [];
    if (!categoryDropdownFilter) return categoryData;
    return categoryData
      .map((cat) => ({
        ...cat,
        subCategory: (cat.subCategory || []).filter((sub) =>
          (sub.name || "").toLowerCase().includes(categoryDropdownFilter)
        ),
      }))
      .filter(
        (cat) =>
          (cat.name || "").toLowerCase().includes(categoryDropdownFilter) ||
          (cat.subCategory && cat.subCategory.length > 0)
      );
  }, [categoryData, categoryDropdownFilter, useLiveCategories]);

  const categoryGridEmpty =
    (useLiveCategories && filteredLiveCategories.length === 0) ||
    (!useLiveCategories && filteredStaticCategories.length === 0);

  const handleProfileMenuItem = (item) => {
    setProfileOpen(false);
    setMobileMenuOpen(false);
    if (item.action === "open-ai-chat") {
      aiChat?.openChat?.();
      return;
    }
    if (item.href) navigate(item.href);
  };

  const profileMenuItemClass =
    "flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-white/60 hover:text-white hover:bg-white/[0.05] transition-all group w-full border-0 bg-transparent cursor-pointer text-left";

  useEffect(() => {
    function handleClick(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (categoriesRef.current && !categoriesRef.current.contains(e.target)) {
        setCategoriesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!categoriesOpen) setCategoryDropdownQuery("");
  }, [categoriesOpen]);

  if (isOrganizerPortal || isAdminPortal || isQRStudio) return null;

  const mainNavAll = [
    { label: "All Events", href: "/tickets", icon: FiCompass },
    { label: "My Tickets", href: "/my-tickets", icon: IoTicketOutline },
    { label: "QR Code Studio", href: "/qr-studio", icon: TbQrcode },
    { label: "Merch", href: "/merch", icon: FiShoppingBag },
    { label: "Creators", href: "/creators", icon: TbSparkles },
    { label: "Create Event", href: "/create-event", icon: TbCalendarPlus, accent: true },
    { label: "InstaPoints", href: "/instapoints", icon: FiZap, gold: true },
  ];

  const mainNav = mainNavAll.filter((item) => {
    if (item.label === "My Tickets") return !!(reduxUser && reduxUser.email);
    return true;
  });

  return (
    <header
      className="sticky top-0 z-[100] bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/[0.06]"
      style={fontStack}
    >
      <div className="w-full px-6 sm:px-8 lg:px-10">
        <div className="flex items-center h-[64px]">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 mr-10">
            <InstaPassLogo size="md" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1">
            <div ref={categoriesRef} className="relative">
              <button
                type="button"
                onClick={() => setCategoriesOpen(!categoriesOpen)}
                className={`flex items-center gap-2.5 px-4 py-2 rounded-xl text-[13px] tracking-wide transition-all duration-200 border-0 bg-transparent cursor-pointer ${categoriesOpen
                    ? "text-white bg-white/[0.08]"
                    : "text-white/50 hover:text-white hover:bg-white/[0.04]"
                  }`}
              >
                <TbLayoutGrid className="w-[15px] h-[15px]" strokeWidth={1.8} />
                Categories
                <FiChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${categoriesOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {categoriesOpen && (
                <div className="absolute top-full left-0 mt-2 w-[720px] bg-[#111111] border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/60 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-5 pt-4 pb-3 border-b border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] text-white" style={{ fontWeight: 700 }}>
                        Popular Categories
                      </span>
                      <div className="flex items-center bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5">
                        <FiSearch className="w-3.5 h-3.5 text-white/30 mr-2" strokeWidth={1.8} />
                        <input
                          type="text"
                          placeholder="Search categories..."
                          value={categoryDropdownQuery}
                          onChange={(e) => setCategoryDropdownQuery(e.target.value)}
                          className="bg-transparent text-[12px] text-white placeholder:text-white/30 outline-none w-36"
                          style={fontStack}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 grid grid-cols-3 gap-4 max-h-[420px] overflow-y-auto">
                    {categoryGridEmpty ? (
                      <div
                        className="col-span-3 py-10 text-center text-[12px] text-white/40"
                        style={fontStack}
                      >
                        No categories match your search.
                      </div>
                    ) : useLiveCategories ? (
                      filteredLiveCategories.map((cat, idx) => {
                        const { icon: CatIcon, color } = resolveLiveCategoryStyle(cat.name, idx);
                        const subs = cat.subCategory || [];
                        return (
                          <div key={cat.id}>
                            <div className="flex items-center gap-2 mb-2.5 px-1">
                              <div
                                className="w-6 h-6 rounded-md flex items-center justify-center"
                                style={{ backgroundColor: `${color}15` }}
                              >
                                <CatIcon className="w-3.5 h-3.5" style={{ color }} strokeWidth={2} />
                              </div>
                              <span
                                className="text-[11px] uppercase tracking-[0.12em]"
                                style={{ fontWeight: 700, color }}
                              >
                                {cat.name}
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              {subs.length > 0 ? (
                                subs.map((sub) => (
                                  <Link
                                    key={sub.id}
                                    to={`/tickets?category=${encodeURIComponent(sub.name)}&categoryId=${sub.id}`}
                                    onClick={() => setCategoriesOpen(false)}
                                    className="block px-2 py-1.5 rounded-lg text-[12px] text-white/50 hover:text-white hover:bg-white/[0.04] transition-all"
                                  >
                                    {sub.name}
                                  </Link>
                                ))
                              ) : (
                                <span className="block px-2 py-1.5 rounded-lg text-[12px] text-white/25">
                                  —
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      filteredStaticCategories.map((cat) => {
                        const CatIcon = cat.icon;
                        return (
                          <div key={cat.title}>
                            <div className="flex items-center gap-2 mb-2.5 px-1">
                              <div
                                className="w-6 h-6 rounded-md flex items-center justify-center"
                                style={{ backgroundColor: `${cat.color}15` }}
                              >
                                <CatIcon className="w-3.5 h-3.5" style={{ color: cat.color }} strokeWidth={2} />
                              </div>
                              <span
                                className="text-[11px] uppercase tracking-[0.12em]"
                                style={{ fontWeight: 700, color: cat.color }}
                              >
                                {cat.title}
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              {cat.subcategories.map((sub) => (
                                <Link
                                  key={sub}
                                  to={`/events?category=${encodeURIComponent(sub)}`}
                                  onClick={() => setCategoriesOpen(false)}
                                  className="block px-2 py-1.5 rounded-lg text-[12px] text-white/50 hover:text-white hover:bg-white/[0.04] transition-all"
                                >
                                  {sub}
                                </Link>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
                    <Link
                      to={useLiveCategories ? "/tickets" : "/events"}
                      onClick={() => setCategoriesOpen(false)}
                      className="text-[12px] text-[#E52324] hover:text-[#ff6b6b] transition-colors"
                      style={{ fontWeight: 600 }}
                    >
                      {useLiveCategories ? "View all events →" : "View All Categories →"}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {mainNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              const isAccent = "accent" in item && item.accent;
              const isGold = "gold" in item && item.gold;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`flex items-center gap-2.5 px-4 py-2 rounded-xl text-[13px] tracking-wide transition-all duration-200 ${isActive
                      ? "text-white bg-white/[0.08]"
                      : "text-white/50 hover:text-white hover:bg-white/[0.04]"
                    }`}
                >
                  <Icon
                    className={`w-[15px] h-[15px] ${isAccent && !isActive ? "text-[#E52324]" : ""} ${isGold && !isActive ? "text-[#D4A84B]" : ""
                      }`}
                    strokeWidth={1.8}
                  />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3 ml-auto">
            <div className="hidden md:flex items-center bg-white/[0.04] border border-white/[0.06] rounded-full px-4 py-[9px]">
              <button
                type="button"
                onClick={() => searchFunction?.()}
                className="p-0 m-0 border-0 bg-transparent cursor-pointer flex items-center text-white/30 hover:text-white/50 mr-2.5"
                aria-label="Search"
              >
                <FiSearch className="w-[15px] h-[15px]" strokeWidth={1.8} />
              </button>
              <input
                type="text"
                placeholder="Search events..."
                value={searchValue}
                onChange={(e) => setSearch?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    searchFunction?.();
                  }
                }}
                className="bg-transparent text-[13px] text-white placeholder:text-white/30 outline-none w-32 lg:w-40"
                style={fontStack}
              />
            </div>

            {/* <button
              type="button"
              className="hidden md:flex relative w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.06] items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.08] transition-all"
            >
              <FiBell className="w-[15px] h-[15px]" strokeWidth={1.8} />
              <span
                className="absolute -top-1 -right-1 w-4 h-4 bg-[#E52324] rounded-full text-[8px] text-white flex items-center justify-center"
                style={{ fontWeight: 700 }}
              >
                3
              </span>
            </button> */}

            <div ref={profileRef} className="relative hidden md:block">
              {displayUser ? (
                <button
                  type="button"
                  onClick={() => setProfileOpen(!profileOpen)}
                  className={`flex items-center justify-center w-9 h-9 rounded-xl border transition-all duration-200 overflow-hidden ${profileOpen
                      ? "bg-white/[0.08] border-white/[0.12]"
                      : "bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08] hover:border-white/[0.10]"
                    }`}
                  aria-expanded={profileOpen}
                  aria-haspopup="true"
                >
                  {displayUser.avatarUrl ? (
                    <img
                      src={displayUser.avatarUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xs text-white" style={{ fontWeight: 700 }}>
                      {displayUser.initials}
                    </span>
                  )}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="px-5 py-2 rounded-xl bg-[#E52324] text-white text-[13px] hover:bg-[#c91f20] transition-colors"
                  style={{ fontWeight: 700 }}
                >
                  Login
                </Link>
              )}

              {profileOpen && displayUser && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-[#111111] border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/60 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="p-4 border-b border-white/[0.05]">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E52324] to-[#ff6b6b] flex items-center justify-center shrink-0 overflow-hidden">
                        {displayUser.avatarUrl ? (
                          <img src={displayUser.avatarUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm text-white" style={{ fontWeight: 700 }}>
                            {displayUser.initials}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] text-white truncate" style={{ fontWeight: 700 }}>
                          {displayUser.name}
                        </div>
                        <div className="text-[11px] text-white/35 truncate">{displayUser.email}</div>
                        <div className="text-[10px] text-white/25 mt-0.5">{displayUser.memberSince}</div>
                      </div>
                    </div>

                    <div className="mt-3 p-2.5 rounded-xl bg-gradient-to-r from-[#E52324]/10 to-transparent border border-[#E52324]/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <FiStar className="w-3.5 h-3.5 text-[#E52324]" style={{ fill: "#E52324" }} />
                          <span
                            className="text-[11px] text-[#E52324] uppercase tracking-wider"
                            style={{ fontWeight: 700 }}
                          >
                            Insta Points
                          </span>
                        </div>
                        <span className="text-sm text-white" style={{ fontWeight: 700 }}>
                          {displayUser.loyaltyPoints.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-1.5">
                    {filteredProfileMenuItems.map((item) => {
                      const Icon = item.icon;
                      const inner = (
                        <>
                          <div className="w-8 h-8 rounded-lg bg-white/[0.04] group-hover:bg-[#E52324]/10 flex items-center justify-center transition-colors">
                            <Icon className="w-4 h-4 group-hover:text-[#E52324] transition-colors" strokeWidth={1.8} />
                          </div>
                          {item.label}
                        </>
                      );
                      if (item.action === "open-ai-chat") {
                        return (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => handleProfileMenuItem(item)}
                            className={profileMenuItemClass}
                          >
                            {inner}
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-white/60 hover:text-white hover:bg-white/[0.05] transition-all group"
                        >
                          {inner}
                        </Link>
                      );
                    })}
                  </div>

                  <div className="p-1.5 border-t border-white/[0.05]">
                    {reduxUser?.role === "admin" && (
                      <Link
                        to="/admin/dashboard"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-white/60 hover:text-white hover:bg-white/[0.05] transition-all group w-full"
                      >
                        <div className="w-8 h-8 rounded-lg bg-white/[0.04] group-hover:bg-[#E52324]/10 flex items-center justify-center transition-colors">
                          <TbLayoutDashboard className="w-4 h-4 group-hover:text-[#E52324] transition-colors" strokeWidth={1.8} />
                        </div>
                        Admin Dashboard
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setProfileOpen(false);
                        dispatch(LogoutUserFunc(navigate));
                      }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-white/35 hover:text-red-400 hover:bg-red-500/5 transition-all group w-full border-0 bg-transparent cursor-pointer text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-white/[0.04] group-hover:bg-red-500/10 flex items-center justify-center transition-colors">
                        <FiLogOut className="w-4 h-4 group-hover:text-red-400 transition-colors" strokeWidth={1.8} />
                      </div>
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {profilePage.isProfilePage ? (
              <ProfileMobileNavDropdown />
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (useSideNavMobile) {
                    setshowmenu(true);
                    setMobileMenuOpen(false);
                  } else {
                    setMobileMenuOpen(!mobileMenuOpen);
                  }
                }}
                className="lg:hidden p-2 text-white/60 hover:text-white"
                aria-label="Open menu"
              >
                {useSideNavMobile ? (
                  <FiMenu className="w-5 h-5" />
                ) : mobileMenuOpen ? (
                  <FiX className="w-5 h-5" />
                ) : (
                  <FiMenu className="w-5 h-5" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {!useSideNavMobile && mobileMenuOpen && (
        <div
          className="lg:hidden bg-[#0a0a0a] border-t border-white/[0.06] px-5 py-4 max-h-[80vh] overflow-y-auto"
          style={fontStack}
        >
          <div className="flex items-center bg-white/[0.04] border border-white/[0.06] rounded-xl px-4 py-2.5 mb-4">
            <button
              type="button"
              onClick={() => {
                searchFunction?.();
                setMobileMenuOpen(false);
              }}
              className="p-0 m-0 border-0 bg-transparent cursor-pointer flex items-center text-white/30 hover:text-white/50 mr-2"
              aria-label="Search"
            >
              <FiSearch className="w-4 h-4" strokeWidth={1.8} />
            </button>
            <input
              type="text"
              placeholder="Search events..."
              value={searchValue}
              onChange={(e) => setSearch?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  searchFunction?.();
                  setMobileMenuOpen(false);
                }
              }}
              className="bg-transparent text-[13px] text-white placeholder:text-white/30 outline-none flex-1"
            />
          </div>

          {displayUser ? (
            <div className="flex items-center gap-3 p-3 mb-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E52324] to-[#ff6b6b] flex items-center justify-center shrink-0 overflow-hidden">
                {displayUser.avatarUrl ? (
                  <img src={displayUser.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-white" style={{ fontWeight: 700 }}>
                    {displayUser.initials}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-white truncate" style={{ fontWeight: 700 }}>
                  {displayUser.name}
                </div>
                <div className="text-[11px] text-white/35 truncate">{displayUser.email}</div>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#E52324]/10">
                <FiStar className="w-3 h-3 text-[#E52324]" style={{ fill: "#E52324" }} />
                <span className="text-[10px] text-[#E52324]" style={{ fontWeight: 700 }}>
                  {displayUser.loyaltyPoints}
                </span>
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center w-full py-2.5 rounded-xl bg-[#E52324] text-white text-[13px] hover:bg-[#c91f20] transition-colors"
                style={{ fontWeight: 700 }}
              >
                Login
              </Link>
            </div>
          )}

          <div className="space-y-0.5">
            <Link
              to="/tickets"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 text-[13px] rounded-xl transition-colors ${location.pathname === "/tickets"
                  ? "text-white bg-white/[0.08]"
                  : "text-white/60 hover:text-white hover:bg-white/[0.04]"
                }`}
            >
              <TbLayoutGrid className="w-4 h-4" strokeWidth={1.8} />
              Categories
            </Link>

            {mainNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              const isAccent = "accent" in item && item.accent;
              const isGold = "gold" in item && item.gold;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 text-[13px] rounded-xl transition-colors ${isActive
                      ? "text-white bg-white/[0.08]"
                      : "text-white/60 hover:text-white hover:bg-white/[0.04]"
                    }`}
                >
                  <Icon
                    className={`w-4 h-4 ${isAccent && !isActive ? "text-[#E52324]" : ""} ${isGold && !isActive ? "text-[#D4A84B]" : ""
                      }`}
                    strokeWidth={1.8}
                  />
                  {item.label}
                </Link>
              );
            })}

            <div className="!my-2 border-t border-white/[0.06]" />

            {filteredProfileMenuItems.map((item) => {
              const Icon = item.icon;
              if (item.action === "open-ai-chat") {
                return (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => handleProfileMenuItem(item)}
                    className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-white/45 hover:text-white hover:bg-white/[0.04] rounded-xl transition-colors w-full border-0 bg-transparent cursor-pointer text-left"
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.8} />
                    {item.label}
                  </button>
                );
              }
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-white/45 hover:text-white hover:bg-white/[0.04] rounded-xl transition-colors"
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                  {item.label}
                </Link>
              );
            })}

            {displayUser && (
              <>
                <div className="!my-2 border-t border-white/[0.06]" />
                {reduxUser?.role === "admin" && (
                  <Link
                    to="/admin/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-white/45 hover:text-white hover:bg-white/[0.04] rounded-xl transition-colors"
                  >
                    <TbLayoutDashboard className="w-4 h-4" strokeWidth={1.8} />
                    Admin Dashboard
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    dispatch(LogoutUserFunc(navigate));
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-white/35 hover:text-red-400 hover:bg-red-500/5 rounded-xl transition-colors w-full border-0 bg-transparent cursor-pointer text-left"
                >
                  <FiLogOut className="w-4 h-4" strokeWidth={1.8} />
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default NewUIMarketplaceHeader;
